import {
  type GridPosition,
  type InteractionSource,
  type SelectionRange,
  type SelectionState,
  type SelectionMode,
} from '../../../state/types';
import { type ReducerResponse, type SelectionAxis } from '../../types';
import { extendRange } from '../range/extend-range';

type ExtendAdjacentRangeParams = {
  state: SelectionState;
  range: SelectionRange;
  axis: SelectionAxis;
  newIndex: number;
  source: InteractionSource;
  mode: SelectionMode;
};

/**
 * Extends an adjacent range to include a new item.
 * @returns A reducer response with updated state and delta
 */
export function extendAdjacentRange({
  state,
  range,
  axis,
  newIndex,
  source,
  mode,
}: ExtendAdjacentRangeParams): ReducerResponse {
  const extendedRange = extendRange({
    range,
    newIndex,
    axis,
  });

  const updatedSelections = state.selections.map((r) =>
    r.id === range.id ? extendedRange : r
  );

  const focusedCell: GridPosition =
    axis === 'row'
      ? { row: { index: newIndex }, col: { index: 0 } }
      : { row: { index: 0 }, col: { index: newIndex } };

  const delta: ReducerResponse['delta'] = {
    kind: 'selectionModified',
    source,
    updated: [extendedRange],
    focusChange: {
      prev: state.focusedCell,
      next: focusedCell,
    },
  };

  return {
    delta,
    next: {
      ...state,
      mode,
      selections: updatedSelections,
      focusedCell,
      lastInteractionSource: source,
    },
  };
}
